import { API_URL } from "../config";

/** Max concurrent POSTs per batch chunk (avoids flooding the backend). */
export const ACCIONES_USUARIO_BATCH_CHUNK_SIZE = 15;

/** POST /acciones-usuario/:id/<route> segments supported by the backend. */
const ALLOWED_ACCIONES_USUARIO_BATCH_ROUTES = Object.freeze([
  "reactivar",
  "bloquear",
  "eliminar-definitivo",
]);

/**
 * @param {unknown} tipoRuta
 * @returns {string} trimmed route segment
 */
function assertValidTipoRuta(tipoRuta) {
  if (typeof tipoRuta !== "string" || tipoRuta.trim() === "") {
    throw new Error(
      `postAccionesUsuarioBatch: tipoRuta must be a non-empty string; allowed: ${ALLOWED_ACCIONES_USUARIO_BATCH_ROUTES.join(", ")}`,
    );
  }
  const trimmed = tipoRuta.trim();
  if (!ALLOWED_ACCIONES_USUARIO_BATCH_ROUTES.includes(trimmed)) {
    throw new Error(
      `postAccionesUsuarioBatch: invalid tipoRuta ${JSON.stringify(tipoRuta)}; allowed: ${ALLOWED_ACCIONES_USUARIO_BATCH_ROUTES.join(", ")}`,
    );
  }
  return trimmed;
}

/**
 * @param {unknown} raw
 * @param {number} index
 * @returns {string}
 */
function normalizeSelectedActionId(raw, index) {
  if (raw == null) {
    const label = raw === null ? "null" : "undefined";
    throw new Error(
      `postAccionesUsuarioBatch: invalid selectedIds entry at index ${index}: expected a non-empty id, got ${label}`,
    );
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed === "") {
      throw new Error(
        `postAccionesUsuarioBatch: invalid selectedIds entry at index ${index}: empty or whitespace-only string`,
      );
    }
    return trimmed;
  }
  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) {
      throw new Error(
        `postAccionesUsuarioBatch: invalid selectedIds entry at index ${index}: non-finite number`,
      );
    }
    return String(raw);
  }
  if (typeof raw === "object") {
    const asString = String(raw).trim();
    if (asString === "" || asString === "[object Object]") {
      throw new Error(
        `postAccionesUsuarioBatch: invalid selectedIds entry at index ${index}: unusable object value`,
      );
    }
    return asString;
  }
  throw new Error(
    `postAccionesUsuarioBatch: invalid selectedIds entry at index ${index}: unsupported type ${typeof raw}`,
  );
}

/**
 * @param {Response} res
 * @returns {Promise<string>}
 */
async function readErrorText(res) {
  const base = `HTTP ${res.status}`;
  try {
    const data = await res.json();
    if (data && typeof data.error === "string" && data.error.trim() !== "") {
      return `${base}: ${data.error.trim()}`;
    }
  } catch {
    /* body not JSON */
  }
  return base;
}

/**
 * @param {unknown[]} selectedIds
 * @param {unknown} tipoRuta
 * @returns {Promise<{ successCount: number; failCount: number; failures: { id: string; message: string }[] }>}
 */
export async function postAccionesUsuarioBatch(selectedIds, tipoRuta) {
  const routeKey = assertValidTipoRuta(tipoRuta);
  const encRoute = encodeURIComponent(routeKey);

  if (!Array.isArray(selectedIds)) {
    throw new Error("postAccionesUsuarioBatch: selectedIds must be an array");
  }

  const normalizedIds = [];
  for (let i = 0; i < selectedIds.length; i += 1) {
    normalizedIds.push(normalizeSelectedActionId(selectedIds[i], i));
  }

  const chunkSize = Math.max(1, Math.min(ACCIONES_USUARIO_BATCH_CHUNK_SIZE, 100));
  const failures = [];
  let successCount = 0;

  for (let offset = 0; offset < normalizedIds.length; offset += chunkSize) {
    const chunk = normalizedIds.slice(offset, offset + chunkSize);
    const settled = await Promise.allSettled(
      chunk.map((id) =>
        (async () => {
          const encId = encodeURIComponent(String(id));
          const res = await fetch(`${API_URL}/acciones-usuario/${encId}/${encRoute}`, {
            method: "POST",
            credentials: "include",
          });
          if (!res.ok) {
            const errMsg = await readErrorText(res);
            throw new Error(String(errMsg));
          }
          return id;
        })(),
      ),
    );
    for (let i = 0; i < settled.length; i += 1) {
      const s = settled[i];
      const id = String(chunk[i] ?? "unknown");
      if (s.status === "fulfilled") {
        successCount += 1;
      } else {
        const reason = s.reason;
        const message = reason instanceof Error ? reason.message : String(reason);
        failures.push({ id, message: message || "Error" });
      }
    }
  }

  return { successCount, failCount: failures.length, failures };
}
